/**
 * KeyedEditors — route-level key wrappers for SkillEditor and PersonaEditor.
 *
 * Both editors live at multiple routes that share the same element shape and
 * tree position in AppRouter (e.g. /skills/new and /skills/:id/edit). Without
 * an explicit React key, React preserves the component instance when navigating
 * between them. useDraftState's render-phase guard only fires when seed is
 * defined — navigating from /skills/:id/edit to /skills/new leaves seedKey null
 * with seed undefined, so the guard never resets the draft. The "new" form
 * shows the previous entity's stale draft.
 *
 * Keying by `id ?? 'new'` gives each entity its own component instance.
 *
 * This module is extracted from AppRouter so both AppRouter and test files can
 * import the real wrapper without pulling in AppRouter's heavy lazy-module tree.
 *
 * `/skills/new|:id/edit` and `/personas/new|:id/edit` are top-level AppRouter
 * routes with no host shell (unlike `/tools/*` under `MiniAppLayout` or
 * `/flow/*` under the Flow shell), so nothing else ever applies the user's
 * stored theme here. `useMiniAppDarkMode()` is idempotent (class toggle) and
 * safe to call a third time alongside those two hosts. It no longer tears
 * `.dark`/`data-theme` down on unmount (that cleanup used to strip the
 * user's theme off the whole app the moment any shell closed, visible as a
 * dark/light flash on navigation) — the theme it applies here is simply left
 * in place when this route unmounts, same as everywhere else it's called.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { useMiniAppDarkMode } from '../../../../../mini-apps/_shared/components/MiniAppLayout';

const SkillEditor = React.lazy(() => import('../skills/SkillEditor'));
const PersonaEditor = React.lazy(() => import('../personas/PersonaEditor'));

/**
 * KeyedSkillEditor — forces SkillEditor to remount when the route entity
 * identity changes (e.g. /skills/:id/edit → /skills/new or vice-versa).
 */
export function KeyedSkillEditor() {
  useMiniAppDarkMode();
  const { id } = useParams<{ id: string }>();
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center h-64 text-content-tertiary dark:text-nightshade-400">
          Loading Skill Editor...
        </div>
      }
    >
      <SkillEditor key={id ?? 'new'} />
    </React.Suspense>
  );
}

/**
 * KeyedPersonaEditor — same pattern as KeyedSkillEditor, for PersonaEditor.
 * Prevents stale draft leakage when navigating between persona routes.
 */
export function KeyedPersonaEditor() {
  useMiniAppDarkMode();
  const { id } = useParams<{ id: string }>();
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center h-64 text-content-tertiary dark:text-nightshade-400">
          Loading Persona Editor...
        </div>
      }
    >
      <PersonaEditor key={id ?? 'new'} />
    </React.Suspense>
  );
}
